// src/contextos/FonteContext.jsx

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import GlobalStyles from "@mui/material/GlobalStyles";
import { CondicaoContext } from "../contexto";
import { PreferenciasProvider } from "./PreferenciasContext";
import TemaProvider from "./TemaProvider";
import { configuracaoFonte } from "../config/preferencias";

const {
  padrao: PERCENTUAL_PADRAO,
  minimo: PERCENTUAL_MINIMO,
  maximo: PERCENTUAL_MAXIMO,
  passo: PASSO_PERCENTUAL,
} = configuracaoFonte;
const PIXELS_POR_FAIXA = 1;
const PREFIXO_PREFERENCIA = "sear:fonte:v1:usuario:";

// Os percentuais são rótulos dos níveis; cada faixa altera 1px nos textos.
const FonteContext = createContext(null);

function normalizarPercentual(valor) {
  if (
    (typeof valor !== "number" && typeof valor !== "string") ||
    (typeof valor === "string" && valor.trim() === "")
  ) {
    return PERCENTUAL_PADRAO;
  }

  const numero = Number(valor);

  if (!Number.isFinite(numero)) {
    return PERCENTUAL_PADRAO;
  }

  const percentual = Math.round(numero / PASSO_PERCENTUAL) * PASSO_PERCENTUAL;

  return Math.min(PERCENTUAL_MAXIMO, Math.max(PERCENTUAL_MINIMO, percentual));
}

function criarChavePreferencia(userId, autenticado) {
  if (!autenticado || userId === null || userId === undefined) {
    return null;
  }

  return `${PREFIXO_PREFERENCIA}${userId}`;
}

function lerPreferencia(chave) {
  if (!chave || typeof window === "undefined") {
    return PERCENTUAL_PADRAO;
  }

  try {
    return normalizarPercentual(window.localStorage.getItem(chave));
  } catch {
    return PERCENTUAL_PADRAO;
  }
}

function salvarPreferencia(chave, percentual) {
  if (!chave || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      chave,
      String(normalizarPercentual(percentual)),
    );
  } catch {
    // O ajuste continua funcionando na sessão se o navegador bloquear o acesso.
  }
}

export function FonteProvider({ children }) {
  const { userId, condicao, loading, role } = useContext(CondicaoContext);

  const chavePreferencia = criarChavePreferencia(
    userId,
    !loading && condicao === false,
  );

  const [preferencia, setPreferencia] = useState(() => ({
    chave: chavePreferencia,
    percentual: lerPreferencia(chavePreferencia),
  }));

  // Ao trocar de usuário, considera imediatamente a preferência do novo acesso.
  const percentual =
    preferencia.chave === chavePreferencia
      ? preferencia.percentual
      : lerPreferencia(chavePreferencia);

  const habilitado = chavePreferencia !== null;
  const ajustePx =
    ((percentual - PERCENTUAL_PADRAO) / PASSO_PERCENTUAL) * PIXELS_POR_FAIXA;

  useEffect(() => {
    setPreferencia({
      chave: chavePreferencia,
      percentual: lerPreferencia(chavePreferencia),
    });
  }, [chavePreferencia]);

  useEffect(() => {
    if (!chavePreferencia || preferencia.chave !== chavePreferencia) {
      return;
    }

    salvarPreferencia(chavePreferencia, preferencia.percentual);
  }, [chavePreferencia, preferencia]);

  useEffect(() => {
    if (!chavePreferencia || typeof window === "undefined") {
      return undefined;
    }

    let armazenamento;

    try {
      armazenamento = window.localStorage;
    } catch {
      return undefined;
    }

    const sincronizarPreferencia = (event) => {
      if (
        event.storageArea !== armazenamento ||
        (event.key !== chavePreferencia && event.key !== null)
      ) {
        return;
      }

      setPreferencia({
        chave: chavePreferencia,
        percentual: lerPreferencia(chavePreferencia),
      });
    };

    window.addEventListener("storage", sincronizarPreferencia);

    return () => {
      window.removeEventListener("storage", sincronizarPreferencia);
    };
  }, [chavePreferencia]);

  const ajustarFonte = useCallback(
    (variacao) => {
      if (!habilitado) {
        return;
      }

      setPreferencia((atual) => {
        const percentualAtual =
          atual.chave === chavePreferencia ? atual.percentual : percentual;

        return {
          chave: chavePreferencia,
          percentual: normalizarPercentual(percentualAtual + variacao),
        };
      });
    },
    [chavePreferencia, habilitado, percentual],
  );

  const aumentarFonte = useCallback(() => {
    ajustarFonte(PASSO_PERCENTUAL);
  }, [ajustarFonte]);

  const diminuirFonte = useCallback(() => {
    ajustarFonte(-PASSO_PERCENTUAL);
  }, [ajustarFonte]);

  const definirFonte = useCallback((novoPercentual) => {
    if (!habilitado) {
      return;
    }

    setPreferencia({
      chave: chavePreferencia,
      percentual: normalizarPercentual(novoPercentual),
    });
  }, [chavePreferencia, habilitado]);

  const restaurarFonte = useCallback(() => definirFonte(PERCENTUAL_PADRAO), [definirFonte]);

  const valor = useMemo(
    () => ({
      percentual,
      ajustePx,
      habilitado,
      podeAumentar: habilitado && percentual < PERCENTUAL_MAXIMO,
      podeDiminuir: habilitado && percentual > PERCENTUAL_MINIMO,
      podeRestaurar: habilitado && percentual !== PERCENTUAL_PADRAO,
      aumentarFonte,
      diminuirFonte,
      definirFonte,
      restaurarFonte,
    }),
    [
      percentual,
      ajustePx,
      habilitado,
      aumentarFonte,
      diminuirFonte,
      definirFonte,
      restaurarFonte,
    ],
  );

  return (
    <FonteContext.Provider value={valor}>
      <GlobalStyles
        styles={{
          ":root": {
            "--sear-ajuste-fonte": `${ajustePx}px`,
            "--sear-ajuste-fonte-admin": role === "admin" ? `${ajustePx}px` : "0px",
            "--sear-ajuste-fonte-sre": role === "superintendent" ? `${ajustePx}px` : "0px",
            "--sear-ajuste-fonte-analista": role === "analyst" ? `${ajustePx}px` : "0px",
            "--sear-ajuste-fonte-escola": role === "user" ? `${ajustePx}px` : "0px",
          },
        }}
      />

      <PreferenciasProvider><TemaProvider>{children}</TemaProvider></PreferenciasProvider>
    </FonteContext.Provider>
  );
}

export function useFonte() {
  const contexto = useContext(FonteContext);

  if (!contexto) {
    throw new Error("useFonte deve ser utilizado dentro de FonteProvider.");
  }

  return contexto;
}
